/**
 * Service de gestion du profil utilisateur
 * Permet de récupérer et mettre à jour les informations du profil (nom, prénom, email)
 */

import { supabase } from '../supabaseClient';

export interface UserProfile {
  id: string;
  email: string;
  nom?: string;
  prenom?: string;
  avatar_url?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Récupère le profil de l'utilisateur actuel
 */
export async function getUserProfile(): Promise<{ data: UserProfile | null; error: any }> {
  try {
    // Récupérer l'utilisateur actuel
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { data: null, error: 'Utilisateur non connecté' };
    }

    // Récupérer le profil depuis la table profiles
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      // Si le profil n'existe pas, créer un profil basique avec l'email
      if (error.code === 'PGRST116') {
        const newProfile = {
          id: user.id,
          email: user.email || '',
          nom: '', // Valeur par défaut vide (NOT NULL dans la table)
          prenom: '', // Valeur par défaut vide (NOT NULL dans la table)
        };
        
        const { data: createdProfile, error: createError } = await supabase
          .from('profiles')
          .insert([newProfile])
          .select()
          .single();

        if (createError) {
          return { data: null, error: createError };
        }

        return { data: createdProfile as UserProfile, error: null };
      }
      
      return { data: null, error };
    }

    return { data: data as UserProfile, error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération du profil:', error);
    return { data: null, error };
  }
}

/**
 * Récupère un profil par id (admin peut lire n'importe quel profil via RLS).
 * Ne crée pas de profil.
 */
export async function getProfileById(
  userId: string,
  fallbackEmail = '',
): Promise<{ data: UserProfile | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return {
          data: {
            id: userId,
            email: fallbackEmail,
            nom: '',
            prenom: '',
          },
          error: null,
        };
      }
      return { data: null, error };
    }

    return { data: data as UserProfile, error: null };
  } catch (error) {
    console.error('Erreur getProfileById:', error);
    return { data: null, error };
  }
}

/**
 * Met à jour le profil de l'utilisateur
 * @param updates Données à mettre à jour (email, nom, prenom, avatar_url)
 */
export async function updateUserProfile(updates: { email?: string; nom?: string; prenom?: string; avatar_url?: string }): Promise<{ data: UserProfile | null; error: any }> {
  try {
    // Récupérer l'utilisateur actuel
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { data: null, error: 'Utilisateur non connecté' };
    }

    // Mettre à jour l'email dans Supabase Auth si fourni
    if (updates.email) {
      const { error: authError } = await supabase.auth.updateUser({
        email: updates.email,
      });

      if (authError) {
        return { data: null, error: authError };
      }
    }

    // Préparer les données à mettre à jour dans la table profiles
    const updateData: any = {};
    if (updates.nom !== undefined) updateData.nom = updates.nom || ''; // NOT NULL, donc chaîne vide si vide
    if (updates.prenom !== undefined) updateData.prenom = updates.prenom || ''; // NOT NULL, donc chaîne vide si vide
    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.avatar_url !== undefined) updateData.avatar_url = updates.avatar_url || null;

    // Ne mettre à jour que si des données sont fournies
    if (Object.keys(updateData).length > 0) {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select()
        .single();

      if (profileError) {
        return { data: null, error: profileError };
      }

      return { data: profileData as UserProfile, error: null };
    }

    // Si aucune mise à jour n'est nécessaire, retourner le profil actuel
    return getUserProfile();
  } catch (error) {
    console.error('Erreur lors de la mise à jour du profil:', error);
    return { data: null, error };
  }
}

